import { useTranslation } from "next-i18next";

import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { cn } from "@/common/utils/css.utils";
import { getPriceInfo } from "@/common/utils/product.utils";
import ProductUtils from "@/components/ProductDetail/utils/ProductUtils";
import { ProductBadge } from "@/components/product/ProductBadge";
import { TradeDiscountOwnerLine } from "@/features/pro-buyer-discount/components/TradeDiscountOwnerLine";
import { ProBuyerPdpPrice } from "@/features/pro-buyer-price/components/ProBuyerPriceMarks";
import { useProductProBuyerPrice } from "@/features/pro-buyer-price/hooks/useProBuyerPrice";
import { useProductReservation } from "@/features/pro-buyer-reservations/hooks/useReservations";
import { ProductVatPriceLine } from "@/features/seller-invoice/components/VatPriceLine";

/**
 * The price line on a product page.
 *
 * ## The realised sale price used to be printed here, and is not any more
 *
 * For a sold listing this component rendered `pdp:pdp-sold-for-new` with
 * `getPriceInfo().soldAtPrice`, which comes from `auction.sold_at`. That is
 * the amount the item actually fetched: per-item transaction data, exposing
 * our sales volume and margin listing by listing, which is precisely what
 * was ruled out for the Resale Index. It was public on every sold PDP.
 *
 * A sold listing now falls through to the ordinary asking price, which was
 * already public for as long as the listing was live. The page still says
 * plainly that the item is sold: `ProductDetailsLabel` renders the Sold
 * label off `ProductUtils.getLabel`, and the bid line stays suppressed
 * through `isSold`.
 *
 * This removal is deliberately NOT behind `SOLD_PAGE_ENABLED`. The flag
 * gates the new sold-page layout, which is a product decision that can
 * wait for a rollout. Publishing the realised price is a data-exposure
 * defect on the page as it stands today, and gating the fix behind a flag
 * that is currently off would leave the exposure live. The redesign
 * strategy says the same: remove it regardless of the redesign.
 */

type ProductDetailsInfoPriceProps = {
  product: ProductBySlugNonNullable;
};

export const ProductDetailsInfoPrice = ({
  product,
}: ProductDetailsInfoPriceProps) => {
  const { t } = useTranslation();

  const allowBid = ProductUtils.allowBid(product);
  const isBidOnly = ProductUtils.isBidOnly(product);
  const isSold = ProductUtils.isSold(product);

  const { discounted, discountPercentage, price, originalPrice, bidPrice } =
    getPriceInfo(product);

  /*
   * WHO-5743. Read client-side after mount, never for the owner (who keeps the
   * owner preview line), so the SSR HTML only ever carries the regular price.
   */
  const { price: proBuyerPrice } = useProductProBuyerPrice(
    product.isOwner ? null : product.id
  );

  // WHO-5947 F1: while reserved nobody can make an offer, the holder included,
  // so the "Bid from" line goes.
  const reservation = useProductReservation(product.id);
  const isReserved =
    !!reservation.reservedUntilDate ||
    reservation.myReservation?.status === "ACCEPTED";

  /*
   * WHO-5142, as in the apps (WHO-6080): a converted price shows the euro it came from small under
   * it. The converted one is a guide; this is what is charged.
   */
  const unconverted = product.auction?.buy_now_price?.unconverted;

  const renderUnconverted = () => {
    if (isBidOnly || !unconverted || (proBuyerPrice && !isSold)) {
      return null;
    }

    return (
      <span
        className="font-body text-sm text-grey-500"
        data-testid="pdp.price.unconverted"
      >
        {t("common:common-price", { price: unconverted })}
      </span>
    );
  };

  const renderBidPrice = () => {
    if (!allowBid || isSold || !bidPrice || isReserved) {
      return null;
    }

    return (
      <span className="font-body text-sm text-grey-500">
        {t("common:common-bid-price", {
          price: bidPrice,
        })}
      </span>
    );
  };

  const renderPrice = () => {
    if (isBidOnly) {
      return null;
    }

    /* With a seller sale, the Pro Buyer sees only this block: no old price, no badge. */
    if (proBuyerPrice && !isSold) {
      return (
        // The Pro Buyer price is in the listing's own currency (its query asks for no display
        // currency), so the regular price next to it is too: one currency per block (WHO-5142).
        <ProBuyerPdpPrice
          proBuyerPrice={proBuyerPrice}
          regularPrice={unconverted ?? price}
        />
      );
    }

    if (discounted) {
      return (
        <span className="flex items-center space-x-1">
          <span className="font-title font-bold text-grey-500 line-through">
            {t("common:common-price", {
              price: originalPrice,
            })}
          </span>
          <span
            data-testid="pdp.price.item"
            className="font-title font-bold text-black"
          >
            {t("common:common-price", {
              price,
            })}
          </span>
          {/* ! beats the parent's space-x-1, which would otherwise win on specificity. */}
          <ProductBadge tone="discount" className="!ml-2 text-xs font-normal">
            -{discountPercentage}%
          </ProductBadge>
        </span>
      );
    }

    return (
      <span
        data-testid="pdp.price.item"
        className="font-title font-bold text-black"
      >
        {t("common:common-price", {
          price,
        })}
      </span>
    );
  };

  return (
    <div className={cn("flex flex-col space-y-2", isSold && "pb-4 md:pb-0")}>
      {renderPrice()}
      {!isBidOnly && <ProductVatPriceLine productId={product.id} />}
      {renderUnconverted()}
      {renderBidPrice()}
      <TradeDiscountOwnerLine
        isOwner={product.isOwner}
        previewPrice={product.tradeDiscountPreviewPrice}
      />
    </div>
  );
};
